import type React from 'react';
import { createContext } from 'react';

import type { UIMessage } from '@tanstack/ai-client';
import type { Conversation } from '@/features/ai-chat/types/chat.types';

export interface ChatState {
  messages: UIMessage[];
  inputMessage: string;
  conversationId: string | null;
  version: number;
  animatedHumanId: string | null;
  animatedText: string | null;
  isInitializing: boolean;
  isSending: boolean;
  ratings: Record<string, -1 | 0 | 1>;
}

export interface ChatActions {
  setMessages: (messages: UIMessage[]) => void;
  setInputMessage: React.Dispatch<React.SetStateAction<string>>;
  handleSend: () => void;
  sendMessage: (text: string, options?: { addHuman?: boolean }) => void;
  setConversationId: (conversationId: string | null) => void;
  restoreConversation: (conversation: Conversation) => void;
  onAnimatedComplete?: () => void;
  setRating: (id: string, score: -1 | 1) => void;
  /**
   * Responds to a tool-approval interrupt. `id` is the tool call's
   * `ToolCallPart.approval.id` (not the tool call id itself) — see
   * `@tanstack/ai-client`'s `ChatClient.addToolApprovalResponse`, which looks
   * up the tool call by matching on `approval.id`.
   */
  addToolApprovalResponse: (response: { id: string; approved: boolean }) => Promise<void>;
  /** Supplies a client tool's result back to the chat (e.g. after a
   * `needsApproval` client tool like `add_to_dashboard` finishes executing). */
  addToolResult: (result: { toolCallId: string; tool: string; output: unknown }) => Promise<void>;
}

export type ChatContextValue = ChatState & ChatActions;

export const ChatStateContext = createContext<ChatState | undefined>(undefined);
export const ChatActionsContext = createContext<ChatActions | undefined>(undefined);
